import { useAppDispatch } from '@/app/redux/hooks';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import {
  getCumulativeGraphemes,
  getGraphemeById,
  getPhaseById,
} from '@/common/utils/scopeAndSequence';
import LevelProgressionIndicator from '@/dashboard/LevelProgressionIndicator';
import { Chip } from '@mui/material';
import { CheckIcon } from '@phosphor-icons/react';
import { CaretSortIcon } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@shadcn/components/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { memo } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';

const levelsByPhase = scopeAndSequence.levels.reduce(
  (acc, level) => {
    const phase = getPhaseById(level.phase);
    if (!phase) {
      return acc;
    }
    acc[phase.phase] = acc[phase.phase] || [];
    acc[phase.phase].push(level as ScopeAndSequenceLevel);
    return acc;
  },
  {} as Record<number, ScopeAndSequenceLevel[]>,
);

interface LevelSelectProps {
  student: Student;
  onLevelChange: (level: number, reset: boolean) => Promise<void>;
}

export default memo(function LevelSelect({
  student,
  onLevelChange,
}: LevelSelectProps) {
  const dispatch = useAppDispatch();

  const selectedLevel = scopeAndSequence.levels.find(
    (l) => l.level === student.level,
  ) as ScopeAndSequenceLevel;

  function handleConfirmLevelChange(level: number) {
    const levelProgress = student.levelsProgress.find(
      (_, idx) => idx === level - 1,
    );
    if (!levelProgress) {
      console.error('Level progress not found for level', level);
      toast.error('Failed to update learner level', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    const { gamesProgress } = levelProgress;
    const levelPartiallyComplete = Object.values(gamesProgress).some(
      (progress) => progress.timesPlayed > 0,
    );

    if (levelPartiallyComplete) {
      setConfirmationDialogueCallback(async (reset) => {
        await onLevelChange(level, reset);
        toast.success('Learner level updated');
      });
      dispatch(
        openConfirmationDialogue({
          title: 'Change Learner Level',
          description:
            'This learner has partially completed this level. Do you want them to start the level again?',
          checkboxOption: {
            label: 'Reset progress for this level',
            default: true,
          },
        }),
      );
    } else {
      onLevelChange(level, false);
      toast.success('Learner level updated');
    }
  }

  function getPreviouslyCompleted(level: ScopeAndSequenceLevel) {
    const levelProgress = student.levelsProgress.find(
      (lp) => lp.levelId === level.id,
    );
    if (!levelProgress) return false;
    return levelProgress.completedPlaythroughs > 0;
  }

  function getChipLabel(level: ScopeAndSequenceLevel) {
    if (level.newGraphemes.length === 0) {
      return 'All Previous';
    }

    const cumulativeGraphemes = getCumulativeGraphemes(level.level);
    if (cumulativeGraphemes.length > level.newGraphemes.length) {
      const additionalGraphemes: string[] = [];
      for (const graphemeId of level.newGraphemes) {
        const grapheme = getGraphemeById(graphemeId);
        if (grapheme) additionalGraphemes.push(grapheme.spelling);
      }
      return `All Previous + ${additionalGraphemes.join(', ')}`;
    }

    const newGraphemes: string[] = [];
    for (const graphemeId of level.newGraphemes) {
      const grapheme = getGraphemeById(graphemeId);
      if (grapheme) newGraphemes.push(grapheme.spelling);
    }
    return newGraphemes.join(', ');
  }

  return (
    <Select
      value={student.level.toString()}
      onValueChange={(value) => handleConfirmLevelChange(parseInt(value))}
    >
      <SelectTrigger
        className="min-w-[400px]"
        onClick={(e) => e.stopPropagation()}
        hideChevron
      >
        <div className="flex items-center justify-between gap-2 w-full min-w-0 overflow-hidden">
          {getPreviouslyCompleted(selectedLevel) && <LevelCompletedIndicator />}
          <LevelTriggerText>
            {`Level ${selectedLevel.level} - ${selectedLevel.description}`}
          </LevelTriggerText>
          <div className="flex items-center gap-2">
            <LevelProgressionIndicator
              currentLevelProgress={student.levelsProgress[student.level - 1]}
            />
            <SelectPrimitive.Icon asChild>
              <CaretSortIcon className="h-4 min-w-4 opacity-50" />
            </SelectPrimitive.Icon>
          </div>
        </div>
      </SelectTrigger>

      <StyledSelectContent>
        {Object.entries(levelsByPhase).map(([phase, levels]) => (
          <SelectGroup key={phase}>
            <SelectLabel>Phase {phase}</SelectLabel>
            {levels.map((level: ScopeAndSequenceLevel) => (
              <SelectItem key={level.id} value={level.level.toString()}>
                <LevelContainer>
                  <div className="flex items-center gap-2">
                    {getPreviouslyCompleted(level) && (
                      <LevelCompletedIndicator />
                    )}
                    <LevelItemText>{`Level ${level.level} - ${level.description}`}</LevelItemText>
                  </div>
                  <Chip
                    label={getChipLabel(level)}
                    size="small"
                    variant="outlined"
                    style={{ marginLeft: 10 }}
                  />
                </LevelContainer>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </StyledSelectContent>
    </Select>
  );
});

const LevelCompletedIndicator = ({ marginLeft }: { marginLeft?: boolean }) => (
  <Tooltip>
    <TooltipTrigger
      className={`grid place-items-center bg-[#7ac362] h-4 w-4 rounded-full p-1 ${
        marginLeft ? 'ml-2' : 'mr-2'
      }`}
      asChild
    >
      <CheckIcon size={12} color="#fff" />
    </TooltipTrigger>

    <TooltipContent>Level previously completed</TooltipContent>
  </Tooltip>
);

const StyledSelectContent = styled(SelectContent)`
  width: var(--radix-select-trigger-width);
`;

const LevelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: 12px;
`;

const LevelTriggerText = styled.div`
  align-items: center;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const LevelItemText = styled.div`
  align-items: center;
  font-size: 14px;
  overflow: visible;
  white-space: normal;
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;
